import { useEffect, useRef, useState } from 'react';
import styles from './products-sold-table.module.css';
import { useDashboardContext, useDashboardDispatch } from '@/contexts/DashboardContext';
import { getSkuSalesBreakdown } from '@/api/seller-analytics';
import { ProductSalesItem, ProductSalesLimit } from '@boxem/core-api-schema';
import Skeleton from '@/components/Skeleton/Skeleton';
import useDebounce from '@/hooks/useDebounce';
import ProductSoldDetailModal from '@/components/ProductsSoldTable/ProductsSoldDetailModal';
import TextInput from '@/components/forms/TextInput/TextInput';
import { createPortal } from 'react-dom';
import { Table, TableColumn, TableRowActionButton, TableState, getTableDataAtPath } from '@/components/Table/Table';
import { LocalStorageKeys, getLocalStorageItem } from '@/utils/localStorageUtils';
import { formatValue } from '@/utils/formatUtils';
import Pagination from '@/components/Pagination/Pagination';
import CopyToClipBoard from '@/components/CopyToClipboard/CopyToClipboard';
import IconSearch from '@/components/icons/IconSearch';

interface ProductSoldTableState {
  sortedColumnId: string | undefined;
  sortDirection: 'asc' | 'desc' | undefined;
  page: number;
  limit: ProductSalesLimit;
}

export default function ProductsSoldTable() {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRowActionRef = useRef<HTMLButtonElement[]>([]);
  const tableName = "productsSoldTable";
  const [tableState, setTableState] = useState<ProductSoldTableState>(() => {
    const tableProfile: TableState = getLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`);

    return {
      sortedColumnId: tableProfile?.sortedColumnId ?? undefined,
      sortDirection: tableProfile?.sortDirection ?? undefined,
      page: 1,
      limit: 10,
    };
  });
  const [filteredProducts, setFilteredProducts] = useState<ProductSalesItem[]>([]);

  const [modalPosition, setModalPosition] = useState({ left: 0, top: 0 });
  const { productSalesBreakdown, activeSnapshotIndex, salesSnapshotCards } = useDashboardContext()
  const dispatch = useDashboardDispatch();

  const selectedInterval = salesSnapshotCards[activeSnapshotIndex].defaultInterval;

  const { tableData, isTableLoading, isDetailOpen } = productSalesBreakdown;

  const handlePageChange = async (newPage: number) => {
    setTableState({
      ...tableState,
      page: newPage,
    });
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setTableState({
      ...tableState,
      page: 1,
      limit: newPageSize as ProductSalesLimit,
    });
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value && tableState.page !== 1) {
      setTableState({
        ...tableState,
        page: 1,
      });
    }
  }

  const handleSortChange = async (columnId: string, direction: 'asc' | 'desc') => {
    console.log('sort change', columnId, direction);
    setTableState({
      ...tableState,
      sortedColumnId: columnId,
      sortDirection: direction,
      page: 1,
    });
  }

  const handleRowActionClick = async (rowIndex: number) => {
    const rowActionButton = tableRowActionRef.current[rowIndex];
    if (rowActionButton && tableData?.products) {
      const selectedListing = tableData.products[rowIndex];
      const rowActionButtonRect = rowActionButton.getBoundingClientRect();
      const tableRect = tableContainerRef.current.getBoundingClientRect();

      const modalLeft = rowActionButtonRect.left - tableRect.left - 300;
      const modalTop = rowActionButtonRect.top - tableRect.top - 150;
      setModalPosition({ left: modalLeft, top: modalTop });
      dispatch({
        type: 'PRODUCT_SALES_BREAKDOWN_ROW_ACTION_CLICKED', data: {
          listing: selectedListing
        }
      });
      const skuSalesResponse = await getSkuSalesBreakdown({ interval: selectedInterval, sku: selectedListing.sku });
      dispatch({ type: 'PRODUCT_SALES_BREAKDOWN_DETAIL_LOADED', data: { detailData: skuSalesResponse } });
    }
  }

  const getFilteredProducts = (products: ProductSalesItem[]) => {
    const columnDataKey = columns?.find(
      (col) => col.id === tableState.sortedColumnId
    )?.dataKey as string;
    const newSortedData = [...products].sort((a, b) => {
      if (tableState.sortedColumnId) {
        const aValue = columnDataKey ? getTableDataAtPath(a, columnDataKey) : 0;
        const bValue = columnDataKey ? getTableDataAtPath(b, columnDataKey) : 0;
        if (tableState.sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      }
      return 0;
    });
    // filter data based on search term in product name, sku, or asin
    if (debouncedSearchTerm && newSortedData) {
      return newSortedData.filter((row) => {
        const productName = row.name.toLowerCase();
        const sku = row.sku.toLowerCase();
        const asin = row.asin.toLowerCase();
        return productName.includes(debouncedSearchTerm.toLowerCase()) ||
          sku.includes(debouncedSearchTerm.toLowerCase()) ||
          asin.includes(debouncedSearchTerm.toLowerCase());
      });
    } else {
      return newSortedData;
    }
  }

  useEffect(() => {
    if (tableData) {
      setFilteredProducts(getFilteredProducts(tableData.products));
    }
  }, [tableData.products, tableState.page, tableState.limit, tableState.sortDirection, tableState.sortedColumnId, debouncedSearchTerm]);

  const columns: TableColumn<ProductSalesItem>[] = [
    {
      title: 'Products',
      dataKey: 'name',
      id: 'products',
      sortable: true,
      width: '19rem',
      pinned: 'left',
      renderRowCell: (row) => (
        <div className={styles.listingItem}>
          <img src={row.getCellData('image')} alt={row.getCellData('name')} className={styles.listingImage} />
          <div className={styles.listingItemDetail}>
            <div className={styles.listingName}>{row.getCellData('name')}</div>
            <CopyToClipBoard value={row.getCellData('sku')} tooltipText="Copy SKU">
              <span className={styles.listingSku}>SKU: <span className="strong">{row.getCellData('sku')}</span></span>
            </CopyToClipBoard>
          </div>
        </div>
      ),
      renderSkeleton: () => <div className={styles.listingItem}>
        <Skeleton type='image' height={32} width={32} />
        <div className={styles.listingItemDetail}>
          <Skeleton type='line-medium' />
          <Skeleton type='line-short' />
        </div>
      </div>
    },
    {
      title: 'Sales',
      dataKey: 'totalSales',
      id: 'sales',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('totalSales'), 'currency'),
    },
    {
      title: 'Units',
      dataKey: 'totalUnits',
      id: 'units',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('totalUnits'), 'number'),
    },
    {
      title: 'Fees',
      dataKey: 'totalAmazonFees',
      id: 'fees',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('totalAmazonFees'), 'currency'),
    },
    {
      title: 'Refunds',
      dataKey: 'totalRefundCosts',
      id: 'refunds',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('totalRefundCosts'), 'currency'),
    },
    {
      title: 'Promotions',
      dataKey: 'totalPromotions',
      id: 'promotions',
      sortable: true,
      visible: false,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('totalPromotions'), 'currency'),
    },
    {
      title: 'Units Shipped',
      dataKey: 'totalShipped',
      id: 'shipped',
      sortable: true,
      visible: false,
      width: '10rem',
      renderRowCell: (row) => formatValue(row.getCellData('totalShipped'), 'number'),
    },
    {
      title: 'Est. Payout',
      dataKey: 'estimatedPayout',
      id: 'estimatedPayout',
      sortable: true,
      visible: false,
      width: '10rem',
      renderRowCell: (row) => formatValue(row.getCellData('estimatedPayout'), 'currency'),
    },
    {
      title: 'Gross Profits',
      dataKey: 'grossProfits',
      id: 'grossProfits',
      sortable: true,
      width: '10rem',
      renderRowCell: (row) => formatValue(row.getCellData('grossProfit'), 'currency'),
    },
    {
      title: '% Refunds',
      dataKey: 'percentageRefunds',
      id: 'percentageRefunds',
      sortable: true,
      visible: false,
      width: '10rem',
      renderRowCell: (row) => formatValue(row.getCellData('percentageRefunds'), 'percent'),
    },
    {
      title: 'Margin',
      dataKey: 'margin',
      id: 'margin',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('margin'), 'percent'),
    },
    {
      title: 'ROI',
      dataKey: 'roi',
      id: 'roi',
      sortable: true,
      width: '7.5rem',
      renderRowCell: (row) => formatValue(row.getCellData('roi'), 'percent'),
    },
    {
      title: '',
      dataKey: undefined,
      id: 'actions',
      sortable: false,
      pinned: 'right',
      width: '3rem',
      renderRowCell: (row) => (
        <TableRowActionButton ref={(el) => (tableRowActionRef.current[row.rowIndex] = el)} onClick={() => handleRowActionClick(row.rowIndex)} />
      ),
    }
  ];

  const filteredProductsPage = filteredProducts.slice((tableState.page - 1) * tableState.limit, tableState.page * tableState.limit);

  return (
    <>
      <Table
        data={filteredProductsPage}
        tableContainerRef={tableContainerRef}
        columns={columns}
        tableName={tableName}
        isLoading={isTableLoading}
        skeletonRows={tableState.limit}
        sortDirection={tableState.sortDirection}
        sortedColumnId={tableState.sortedColumnId}
        onSort={handleSortChange}
        showSort
        header={<TextInput
          className={styles.searchInput}
          placeholder={"Product Name, SKU, or ASIN"}
          prefix={<IconSearch />}
          value={searchTerm}
          inputAutofocus
          onChange={(value) => handleSearchChange(value)}
        />}
        footer={<Pagination
          total={filteredProducts.length || 0}
          pageSize={tableState.limit}
          onChangePageSize={handlePageSizeChange}
          page={tableState.page}
          onChangePage={handlePageChange}
          showPageSelector={false}
          pageSizeOptions={[5, 10, 20]}
        />} />

      {isDetailOpen && createPortal(
        <ProductSoldDetailModal modalPosition={modalPosition} />,
        tableContainerRef.current)}
    </>
  );
}
